import type { CSSProperties } from 'react';
import type { Customer } from '@setapart/shared';
import { FONTS, COLORS } from '@setapart/shared';
import { Link2, Settings as SettingsIcon, TrendingUp, Package, CreditCard } from 'lucide-react';

interface Props {
  customer: Customer;
  onConnect: () => void;
  onConnectStripe?: () => void;
}

export function Dashboard({ customer, onConnect, onConnectStripe }: Props) {
  const connectedShop = new URL(window.location.href).searchParams.get('connected');

  return (
    <div>
      <div style={{ fontSize: 10, letterSpacing: '0.4em', textTransform: 'uppercase', fontWeight: 700, color: COLORS.accent, marginBottom: 8 }}>
        DASHBOARD
      </div>
      <h1 style={{ fontFamily: FONTS.display, fontSize: 32, color: COLORS.ink, margin: 0, fontStyle: 'italic', fontWeight: 500 }}>
        Welcome, {customer.email.split('@')[0]}
      </h1>

      {connectedShop && (
        <div style={banner}>
          Shopify store <strong>{connectedShop}</strong> connected. Catalog backfill is in progress — products will appear in a few minutes.
        </div>
      )}

      <div style={gridStyle}>
        <button onClick={onConnect} style={cardStyle} data-testid="card-connect">
          <Link2 size={22} color={COLORS.ink} />
          <div style={cardTitle}>Connect Shopify</div>
          <div style={cardDesc}>Install the Set Apart app on your Shopify admin.</div>
        </button>
        <button
          onClick={() => onConnectStripe?.()}
          disabled={!onConnectStripe}
          style={{ ...cardStyle, cursor: onConnectStripe ? 'pointer' : 'default', opacity: onConnectStripe ? 1 : 0.7 }}
          data-testid="card-connect-stripe"
        >
          <CreditCard size={22} color={COLORS.ink} />
          <div style={cardTitle}>Connect Stripe</div>
          <div style={cardDesc}>
            Accept payments. Set Apart's commission comes off the top — no manual payouts.
          </div>
        </button>
        <div style={cardStyle}>
          <Package size={22} color={COLORS.ink} />
          <div style={cardTitle}>Catalog</div>
          <div style={cardDesc}>Products sync every 6 hours. Webhook updates land instantly.</div>
        </div>
        <div style={cardStyle}>
          <TrendingUp size={22} color={COLORS.ink} />
          <div style={cardTitle}>Performance</div>
          <div style={cardDesc}>Clicks, checkouts, and commissions. Live once your first order lands.</div>
        </div>
        <div style={cardStyle}>
          <SettingsIcon size={22} color={COLORS.ink} />
          <div style={cardTitle}>Settings</div>
          <div style={cardDesc}>Contact info, tier, notifications.</div>
        </div>
      </div>
    </div>
  );
}

const banner: CSSProperties = {
  marginTop: 20,
  padding: 14,
  background: 'rgba(28,58,46,0.08)',
  border: `1px solid rgba(28,58,46,0.25)`,
  borderRadius: 12,
  fontSize: 13,
  color: COLORS.ink,
};
const gridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
  gap: 12,
  marginTop: 24,
};
const cardStyle: CSSProperties = {
  padding: 18,
  borderRadius: 16,
  background: '#fff',
  border: `1px solid ${COLORS.sand}`,
  textAlign: 'left',
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  fontFamily: FONTS.body,
};
const cardTitle: CSSProperties = {
  fontFamily: FONTS.display,
  fontSize: 18,
  color: COLORS.ink,
  fontWeight: 500,
  fontStyle: 'italic',
  marginTop: 6,
};
const cardDesc: CSSProperties = {
  fontSize: 12,
  color: COLORS.muted,
  lineHeight: 1.4,
};
